"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Separator = Separator;
exports.Kbd = Kbd;
exports.Progress = Progress;
exports.AvatarImage = AvatarImage;
exports.AvatarFace = AvatarFace;
exports.Avatar = Avatar;
const jsx_runtime_1 = require("react/jsx-runtime");
const cn_1 = require("../lib/cn");
const blobatar_1 = require("./blobatar");
const color_1 = require("../lib/color");
const tooltip_1 = require("./tooltip");
function Separator({ orientation = 'horizontal', className, }) {
    return ((0, jsx_runtime_1.jsx)("div", { role: "separator", "aria-orientation": orientation, className: (0, cn_1.cn)('shrink-0 bg-border', orientation === 'vertical' ? 'h-full w-px' : 'h-px w-full', className) }));
}
/** ⚠️ `font-sans`, not `` — the mockup's call, and a keycap is a label, not code. */
function Kbd({ className, children, ...rest }) {
    return ((0, jsx_runtime_1.jsx)("kbd", { className: (0, cn_1.cn)('pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-sans text-[10px] font-medium text-muted-foreground', className), ...rest, children: children }));
}
/**
 * ⚠️ **`role="progressbar"` with its value, which the mockup omits.** Its version is a pair of
 * divs: visually right, and invisible to a screen reader. A progress bar whose only output is
 * a width is a progress bar that reports nothing.
 */
function Progress({ value, label, color, className }) {
    const clamped = Math.max(0, Math.min(100, value));
    return ((0, jsx_runtime_1.jsx)("div", { role: "progressbar", "aria-label": label, "aria-valuenow": Math.round(clamped), "aria-valuemin": 0, "aria-valuemax": 100, className: (0, cn_1.cn)('relative h-1.5 w-full overflow-hidden rounded-full bg-primary/15', className), children: (0, jsx_runtime_1.jsx)("div", { className: "h-full w-full rounded-full transition-transform duration-300 ease-[var(--ease-out-strong)]", style: {
                transform: `translateX(${String(clamped - 100)}%)`,
                background: color ?? 'var(--primary)',
            } }) }));
}
/**
 * The picture that sits over a set of initials.
 *
 * ⚠⚠️ **ONE DEFINITION, FOUR CALLERS — `Avatar`, the sidebar chip, a board card and a list
 * cell.** Each of those draws its own circle at its own measured size, and C22 needed a picture in
 * all four; a copy per caller is four places for the fallback behaviour to drift.
 *
 * ⚠️ **The initials stay UNDERNEATH rather than being replaced.** An `<img>` that fails to load
 * — a revoked object URL, a network blip, a file the scanner has since refused — leaves a blank
 * circle; with the fallback behind it, the worst case is what the product looked like before there
 * were pictures at all.
 *
 * ⚠️ `alt=""`, because every caller already carries the accessible name on the wrapper.
 */
function AvatarImage({ src }) {
    if (src === undefined)
        return null;
    return (0, jsx_runtime_1.jsx)("img", { src: src, alt: "", className: "absolute inset-0 size-full object-cover" });
}
/**
 * What sits INSIDE an avatar circle: the generated face, or the initials when there is no seed.
 *
 * ⚠️⚠️ **FIVE PLACES DRAW THEIR OWN CIRCLE AND THIS IS THE ONE THING INSIDE IT.** `Avatar`, the
 * sidebar chip, a board card, a list cell and the settings table each measure their own disc at
 * their own size -- that part is deliberate and stays. What must not be five copies is the
 * *decision*: seed wins over initials, and both sit UNDER the photo so an `<img>` that fails to
 * load reveals a face rather than a blank disc.
 *
 * ⚠️ **No seed means nothing changes**, which is what let the blobatar arrive as a prop rather
 * than as a rewrite of thirty call sites -- and what keeps a face off the things that are not
 * people. A project swatch and a client's mark come through here too, and they pass initials.
 */
function AvatarFace({ seed, fallback, size = 20, animate = false, svgRef, }) {
    if (seed === undefined)
        return fallback;
    /**
     * ⚠️⚠️ **THE FACE GETS A WHITE PLATE, WHICH COVERS THE IDENTITY HUE UNDER IT.** Every
     * circle in this app fills itself with somebody's colour, because that colour is what two
     * letters need to be legible. A blobatar needs the opposite: it is drawn in its own palette,
     * derived from its own seed, and a coloured ground behind it is a second hue competing with the
     * first — worst where the two are close, which is exactly when it looks like a rendering fault
     * rather than a choice. A purple blob on the primary purple was how this was found (23 Sep).
     *
     * ⚠️⚠️ **`--on-id-light`, AND NOT `--background`.** The obvious token follows the theme,
     * which would put a near-black disc behind a face drawn in a palette designed against a pale
     * one — in the theme where its contrast is already tightest. `--on-id-light` is the system's
     * one deliberately THEME-INDEPENDENT pale value, and `tokens.css` states the reason in terms
     * that apply here unchanged: *"the hue underneath does not change between themes, so neither can
     * the thing written on it"*. A blobatar's palette is exactly such a hue. It is named as a
     * foreground because its other use is initials on a coloured disc; the value is what is being
     * borrowed, and borrowing the value is what keeps this off a literal.
     *
     * ⚠️⚠️ **`size-full` AND ITS OWN CLIP, NEVER `absolute inset-0`, AND THAT COST A RELEASE.**
     * This was written absolute, on the reasoning that it then covers the caller's coloured plate
     * rather than sitting inside it — true, and it carried a note claiming *"every caller's circle
     * is `relative` and `overflow-hidden`"*. Four of them were not: the logging, resource and leave
     * dashboards draw their own discs, and those spans are `grid size-6 place-items-center
     * rounded-full` with neither. An absolutely positioned child then resolves against whatever
     * ancestor happens to be positioned — a table cell, a row, the page — so the face inflated to
     * that box's size and nothing clipped it. Enormous faces over the whole first column.
     *
     * ⚠️⚠️ **THE FIX IS THAT THIS COMPONENT NOW ASKS NOTHING OF ITS PARENT.** `size-full` fills
     * whatever box it is given, and the clip is on this element rather than borrowed from an
     * ancestor. A component whose correctness depends on a class somebody remembered to put on its
     * container is a component that breaks silently on the fifth call site, and it did.
     *
     * ⚠️ `AvatarImage` is still the sibling AFTER this in the four primitives that draw one, so an
     * uploaded photo still wins.
     */
    return ((0, jsx_runtime_1.jsx)("span", { className: "bg-on-id-light grid size-full place-items-center overflow-hidden rounded-full", children: (0, jsx_runtime_1.jsx)(blobatar_1.Blobatar, { seed: seed, size: size, animate: animate, ...(svgRef === undefined ? {} : { svgRef }) }) }));
}
function Avatar({ fallback, seed, animate = false, color, size = 24, title, className, src, }) {
    /* ⚠️ The name is the kit's tooltip, never the browser's `title` (20 Oct 2026). */
    const tip = (0, tooltip_1.useTooltip)(title);
    return ((0, jsx_runtime_1.jsxs)("span", { className: (0, cn_1.cn)('relative flex shrink-0 overflow-hidden rounded-full', className), style: { width: size, height: size }, role: "img", "aria-label": title, ...tip.triggerProps({}), children: [(0, jsx_runtime_1.jsx)("span", { className: "flex size-full items-center justify-center rounded-full font-medium", style: {
                    background: color ?? 'var(--primary)',
                    /* ⚠️ The foreground is MEASURED against the colour, because the colour is data — a
                     * project swatch or a member's hue, not a token. `readableOn` picks the better of the
                     * two `--on-id*` foregrounds; a fixed one measured 1.36:1 in the browser. */
                    color: color === undefined ? 'var(--primary-foreground)' : (0, color_1.readableOn)(color),
                    fontSize: Math.max(9, Math.round(size * 0.38)),
                }, children: (0, jsx_runtime_1.jsx)(AvatarFace, { seed: seed, fallback: fallback, size: size, animate: animate }) }), (0, jsx_runtime_1.jsx)(AvatarImage, { src: src }), tip.bubble] }));
}
//# sourceMappingURL=misc.js.map